import React, {
  PropsWithChildren,
  ReactNode,
  useRef,
  useCallback,
} from 'react';
import { Portal, Paper, Box } from '@mui/material';
import { Gripper } from 'spw-components/global/Gripper';
import GrabAreaGrayIcon from 'spw-static/images/grab-area-gray.svg';
import Draggable, {
  DraggableBounds,
  DraggableData,
  DraggableEvent,
} from 'react-draggable';
import styled from 'styled-components';
import { colors } from 'spw-theme';

interface Props extends PropsWithChildren {
  anchorEl: Element;
  title: ReactNode;
  open: boolean;
  defaultPosition?: { top: number; left: number };
  height?: number;
  width?: number;
  onStop?: (e: DraggableEvent, data: DraggableData) => void | false;
  bounds?: DraggableBounds | string | false;
  gripperWrapperStyles?: React.CSSProperties;
  processRefNode?: (node: HTMLElement) => void;
}

const DialogPaper = styled(Paper)`
  .drag {
    background-color: ${colors.containerBorder};

    cursor: move;

    & > img {
      width: 16px;
    }
  }
`;

const GrabArea = styled(Box)`
  && {
    .gripper {
      border-radius: 3px 3px 0 0;
    }

    .bottom-gripper {
      border-radius: 0 0 3px 3px;
    }
  }
`;

const DraggableDialog = ({
  anchorEl,
  title,
  children,
  open,
  defaultPosition,
  height,
  width,
  onStop,
  bounds = '#react-container',
  gripperWrapperStyles,
  processRefNode,
}: Props) => {
  const initialPosition = defaultPosition ?? {
    top: anchorEl.clientTop,
    left: anchorEl.clientLeft,
  };
  const nodeRef = useRef<HTMLDivElement>(null);

  const dialogRef = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        nodeRef.current = node;
        if (processRefNode) {
          processRefNode(node);
        }
      }
    },
    [processRefNode]
  );

  return (
    <Portal>
      <Draggable
        handle="#draggable-dialog-content"
        bounds={bounds}
        nodeRef={nodeRef}
        onStop={onStop}
      >
        <DialogPaper
          ref={dialogRef}
          sx={{
            position: 'fixed',
            zIndex: 102,
            ...initialPosition,
            display: open ? 'block' : 'none',
            height,
            width,
          }}
        >
          <div style={{ height: '100%' }}>
            <GrabArea id="draggable-dialog-content">
              <Gripper
                wrapperStyles={gripperWrapperStyles}
                GrabIcon={GrabAreaGrayIcon}
                className="drag gripper"
              />
              {title}
            </GrabArea>

            {children}
          </div>
        </DialogPaper>
      </Draggable>
    </Portal>
  );
};

export default DraggableDialog;
